import { ReactNode, useState } from "react";
import {
  Box,
  CSSObject,
  Drawer,
  IconButton,
  List,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { useLocation } from "../../../../modules/common";
import { MediumCustomIcon } from "../../../../modules/components/layer00";
import type { INavBarItem } from "../../../../modules/components/layer01";
import { NavBarItem } from "../../../../modules/components/layer01";

type NavBarProps = {
  navGroups: {
    label: string;
    items: INavBarItem[];
  }[];
  bottomBottom?: {
    label: string;
    icon: ReactNode;
    path?: string;
    onClick: () => void;
    loading?: boolean;
  };
  extraSection?: ReactNode;
};
const drawerWidth = "15.75rem";

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  background:
    theme.palette.mode === "dark"
      ? "var(--team-app-dark-stripe-base)"
      : `repeating-linear-gradient(var(--team-app-nav-divider) 0px, var(--team-app-nav-divider) 5px, var(--team-app-stripe-filter-base) 5px, var(--team-app-stripe-filter-base) 6px)`,
  overflow: "visible",
  borderRight: `1px solid ${
    theme.palette.mode === "dark"
      ? "var(--team-app-dark-border)"
      : "var(--team-app-filter-active-border)"
  }`,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: `calc(${theme.spacing(8)} + 7px)`,
  height: "100vh",
  background:
    theme.palette.mode === "dark"
      ? "var(--team-app-gradient-stripe-dark)"
      : "var(--team-app-gradient-stripe-filter)",
  overflow: "visible",
  borderRight: `1px solid ${
    theme.palette.mode === "dark"
      ? "var(--team-app-dark-border)"
      : "var(--team-app-filter-active-border)"
  }`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 7px)`,
    height: "100vh",
  },
});

export const NavBar = (props: NavBarProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const location = useLocation();

  const [open, setOpen] = useState(true);

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        flexShrink: 0,
        ...(open && {
          ...openedMixin(theme),
          "& .MuiDrawer-paper": {
            ...openedMixin(theme),
            height: "-webkit-fill-available",
          },
        }),
        ...(!open && {
          ...closedMixin(theme),
          "& .MuiDrawer-paper": closedMixin(theme),
        }),
      }}
    >
      <Box
        sx={{
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background: isDark
            ? "var(--team-app-gradient-stripe-dark)"
            : `repeating-linear-gradient(-45deg, white 0px 5px, var(--team-app-stripe-light) 5px 6px)`,
        }}
      >
        <List
          sx={{
            width: open ? drawerWidth : "100%",
            // Only the list scrolls, so the bottom button stays pinned to the bottom.
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {props.navGroups.map((group) => {
            const collapsedLabel =
              group.label.length > 5
                ? `${group.label.slice(0, 5)}...`
                : group.label;

            return (
              <Box key={group.label} paddingTop="1.5rem" paddingBottom="1.5rem">
                <Typography
                  variant="navBarGroupTitle"
                  paddingLeft={open ? "1.75rem" : 0}
                  paddingRight={open ? 0 : ".5rem"}
                  sx={{
                    display: "block",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textAlign: open ? "left" : "center",
                  }}
                >
                  {open ? group.label : collapsedLabel}
                </Typography>
                <Box paddingTop=".5rem">
                  <Box
                    paddingTop={0}
                    paddingLeft={0}
                    paddingRight={open ? 2.5 : 0}
                  >
                    {group.items.map((item) => (
                      <NavBarItem
                        key={item.path ?? item.label}
                        isOpen={open}
                        label={item.label}
                        icon={item.icon}
                        path={item.path}
                        disabled={item.disabled}
                        isSelected={
                          !!location.pathname.includes(item.path ?? "")
                        }
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          })}
          {open && props.extraSection}
        </List>

        {props.bottomBottom && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              if (props.bottomBottom?.loading) return;
              props.bottomBottom?.onClick();
            }}
            sx={{
              flexShrink: 0,
              paddingTop: "1rem",
              marginBottom: "1rem",
            }}
          >
            <NavBarItem
              label={open ? props.bottomBottom.label : ""}
              icon={props.bottomBottom.icon}
              path={props.bottomBottom.path}
              onClick={() => props.bottomBottom?.onClick()}
              isButton={true}
              loading={props.bottomBottom.loading}
            />
          </Box>
        )}
      </Box>

      <Box
        position="absolute"
        bottom="15px"
        left={open ? "232px" : "50px"}
        sx={{
          transform: `rotate(${open ? "0deg" : "180deg"})`,
          zIndex: 1000000000,
        }}
      >
        <IconButton
          onClick={() => setOpen((prev) => !prev)}
          className={isDark ? undefined : "bg-white"}
          sx={{
            background: isDark ? "var(--team-app-dark-border)" : undefined,
          }}
        >
          <MediumCustomIcon
            icon="CHEVRONLEFT"
            stroke={isDark ? "var(--team-app-stripe-mid)" : undefined}
          />
        </IconButton>
      </Box>
    </Drawer>
  );
};
